/**
 * Input Sanitization Utilities
 * Provides XSS protection and input cleaning for React components
 */

// HTML entities that should be escaped
const HTML_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;'
};

// Dangerous patterns to remove
const DANGEROUS_PATTERNS = [
  /javascript:/gi,
  /vbscript:/gi,
  /data:/gi,
  /on\w+\s*=/gi, // Event handlers like onclick=
  /<script/gi,
  /<\/script>/gi,
  /<iframe/gi,
  /<\/iframe>/gi,
  /<object/gi,
  /<\/object>/gi,
  /<embed/gi,
  /<\/embed>/gi,
  /<link/gi,
  /<meta/gi,
  /<style/gi,
  /<\/style>/gi
];

/**
 * Escape HTML entities to prevent XSS
 */
export const escapeHtml = (str) => {
  if (typeof str !== 'string') return str;
  
  return str.replace(/[&<>"'`=\/]/g, (char) => HTML_ENTITIES[char] || char);
};

/**
 * Remove dangerous patterns from input
 */
export const removeDangerousPatterns = (str) => {
  if (typeof str !== 'string') return str;
  
  let cleaned = str;
  DANGEROUS_PATTERNS.forEach(pattern => {
    cleaned = cleaned.replace(pattern, '');
  });
  
  return cleaned;
};

/**
 * Sanitize input for safe display
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove dangerous patterns first
  let sanitized = removeDangerousPatterns(input);
  
  // Escape HTML entities
  sanitized = escapeHtml(sanitized);
  
  // Remove excessive whitespace
  sanitized = sanitized.trim().replace(/\s+/g, ' ');
  
  return sanitized;
};

/**
 * Light sanitization for form inputs (preserves user input)
 */
export const sanitizeFormInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove dangerous patterns but preserve user input
  let sanitized = removeDangerousPatterns(input);
  
  // Don't escape HTML entities for form inputs - too aggressive
  // Just trim whitespace
  sanitized = sanitized.trim();
  
  return sanitized;
};

/**
 * Sanitize form data object
 */
export const sanitizeFormData = (formData) => {
  const sanitized = {};
  
  for (const [key, value] of Object.entries(formData)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * Validate and sanitize email input
 */
export const sanitizeEmail = (email) => {
  if (!email) return '';
  
  // Don't validate here, just sanitize - let validation components handle validation
  // Sanitize the email
  return sanitizeInput(email.toLowerCase().trim());
};

/**
 * Validate and sanitize password input
 */
export const sanitizePassword = (password) => {
  if (!password) return '';
  
  // Remove dangerous patterns but preserve password characters
  let sanitized = removeDangerousPatterns(password);
  
  // Don't escape HTML entities for passwords as they might be legitimate
  // Just remove dangerous patterns and trim
  return sanitized.trim();
};

/**
 * Create safe HTML content (for display purposes only)
 */
export const createSafeHtml = (content) => {
  if (typeof content !== 'string') return content;
  
  const sanitized = sanitizeInput(content);
  
  // For display, we can use dangerouslySetInnerHTML with sanitized content
  return {
    __html: sanitized
  };
};

/**
 * Validate input length and content
 */
export const validateInputLength = (input, maxLength = 255) => {
  if (!input) return { isValid: true, message: '' };
  
  if (input.length > maxLength) {
    return {
      isValid: false,
      message: `Input must be less than ${maxLength} characters`
    };
  }
  
  return { isValid: true, message: '' };
};

/**
 * Check for suspicious input patterns
 */
export const detectSuspiciousInput = (input) => {
  if (typeof input !== 'string') return { isSuspicious: false, reason: '' };
  
  const suspiciousPatterns = [
    { pattern: /<script/i, reason: 'Script tags detected' },
    { pattern: /javascript:/i, reason: 'JavaScript protocol detected' },
    { pattern: /on\w+\s*=/i, reason: 'Event handlers detected' },
    { pattern: /<iframe/i, reason: 'Iframe tags detected' },
    { pattern: /<object/i, reason: 'Object tags detected' },
    { pattern: /<embed/i, reason: 'Embed tags detected' }
  ];
  
  for (const { pattern, reason } of suspiciousPatterns) {
    if (pattern.test(input)) {
      return { isSuspicious: true, reason };
    }
  }
  
  return { isSuspicious: false, reason: '' };
};

/**
 * Input sanitization service
 */
export const InputSanitization = {
  escapeHtml,
  removeDangerousPatterns,
  sanitizeInput,
  sanitizeFormData,
  sanitizeEmail,
  sanitizePassword,
  createSafeHtml,
  validateInputLength,
  detectSuspiciousInput
};

export default InputSanitization;
